<template>
  <div ref="rootEl" class="grid min-w-0 gap-2" data-testid="address-lookup-fields">
    <!-- THE PILL. An answered address folds to one line (the §55 grammar the
         person and place answers already follow): a found house = a chosen
         card, teal border on the state wash with the check; a stored address
         that merely opened settled = the same pill, grey pin, no check — the
         check means « answered », never « present »; « sans localisation » =
         the pin-off glyph. « changer » brings the inputs back, prefilled. -->
    <!-- A grid item shrinks below its text only with min-w-0; the line then
         WRAPS before the town (each part is kept whole) rather than truncating
         an address into an ellipsis nobody can read. -->
    <div
      v-if="collapsed"
      class="flex min-w-0 items-start justify-between gap-3 rounded-lg border px-3 py-2"
      :class="pillTone === 'found' ? 'border-state bg-state-wash' : ''"
      data-testid="address-pill"
    >
      <span class="flex min-w-0 items-start gap-2 text-sm">
        <Check v-if="pillTone === 'found'" class="mt-0.5 h-4 w-4 shrink-0 text-state-ink" />
        <MapPinOff v-else-if="pillTone === 'unlocated'" class="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <MapPin v-else class="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <!-- The separators sit OUTSIDE the no-wrap spans: inside them the line
             had no break point and ran past the card. -->
        <span class="min-w-0 break-words leading-5">
          <span class="font-medium">{{ pillLine }}</span>
          <template v-if="pillCity">
            <span class="text-muted-foreground"> · </span><span class="whitespace-nowrap text-muted-foreground">{{ pillCity }}</span>
          </template>
          <template v-if="house?.weak">
            <span class="text-muted-foreground"> · </span><span class="whitespace-nowrap font-medium text-amber-700 dark:text-amber-400">{{ t('addressLookup.weakGrade') }}</span>
          </template>
          <span v-if="pillTone === 'unlocated'" class="text-muted-foreground"> · {{ saveVerb === 'create' ? t('addressLookup.willCreateUnlocated') : t('addressLookup.willSaveUnlocated') }}</span>
        </span>
      </span>
      <button type="button" class="mt-0.5 shrink-0 text-xs underline" data-testid="address-change" @click="expand()">
        {{ t('addressLookup.change') }}
      </button>
    </div>

    <!-- THE PAIR. The registry's own key: a civic number and a postal code name
         one street 94% of the time in town, so nobody types a street here. -->
    <!-- items-start: an error under one cell must not stretch the other and
         slide its input down — the two inputs stay level, the message only
         lengthens its own column. -->
    <div v-show="!placeCollapsed && !collapsed" class="grid grid-cols-[2fr_3fr] items-start gap-2">
      <FormField v-slot="{ componentField }" :name="`${fieldPrefix}civicNumber`">
        <FormItem class="grid gap-1 space-y-0">
          <FormLabel v-if="labels">{{ t('serviceLocations.fields.civicNumber') }}</FormLabel>
          <FormControl>
            <Input
              v-bind="componentField" :class="inputClass" inputmode="numeric"
              :placeholder="t('serviceLocations.fields.civicNumber')" data-testid="address-civic-number"
              @input="lookup.touch()" @blur="lookup.check()"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" :name="`${fieldPrefix}postalCode`">
        <FormItem class="grid gap-1 space-y-0">
          <FormLabel v-if="labels">{{ t('serviceLocations.fields.postal_code') }}</FormLabel>
          <FormControl>
            <Input
              v-bind="componentField" :class="inputClass" autocapitalize="characters"
              :placeholder="t('serviceLocations.fields.postal_code')" data-testid="address-postal-code"
              @input="lookup.onPostalInput()" @blur="lookup.check()"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- THE STREET, the exception's fields: open when the pair cannot settle
         the house (ask_street) or the caller has no postal code (the door). -->
    <!-- A real element carries the v-show: FormField is renderless, and a
         directive on it has nothing to hide. -->
    <div v-if="streetOpen" v-show="!placeCollapsed && !collapsed" class="grid gap-2">
      <FormField v-slot="{ componentField }" :name="`${fieldPrefix}street`">
        <FormItem class="grid gap-1 space-y-0">
          <FormLabel v-if="labels">{{ t('serviceLocations.fields.street') }}</FormLabel>
          <FormControl>
            <Input
              v-bind="componentField" :class="inputClass"
              :placeholder="t('serviceLocations.fields.street')" data-testid="address-street"
              @input="lookup.touch()" @blur="lookup.check()"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" :name="`${fieldPrefix}city`">
        <FormItem class="grid gap-1 space-y-0">
          <FormLabel v-if="labels">{{ t('serviceLocations.fields.city') }}</FormLabel>
          <FormControl>
            <Input
              v-bind="componentField" :class="inputClass"
              :placeholder="t('serviceLocations.fields.city')" data-testid="address-city"
              @input="lookup.touch()" @blur="lookup.check()"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- The answer, under the row. One element per state; the stopping states
         scroll themselves into view because the save button is below the fold. -->
    <div ref="answerEl">
      <!-- Street mode waiting for its town: a street alone would pin the wrong
           town, so nothing is asked — and the row SAYS so instead of staying
           silent on blur (his QA). -->
      <p
        v-if="needsTown"
        class="text-xs text-muted-foreground"
        data-testid="address-needs-town"
      >
        {{ t('addressLookup.validation.cityOrPostalRequired') }}
      </p>

      <!-- geocoding: the spinner sits WITH the inputs, never on the button. -->
      <p
        v-else-if="state === 'geocoding'"
        class="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="address-geocoding"
      >
        <Loader2 class="h-3 w-3 animate-spin" />
        {{ t('addressLookup.locating') }}
      </p>

      <!-- choose: two to six streets have that number. A human is present, so
           this is a question, not the import's refusal. -->
      <div
        v-else-if="state === 'choose'"
        class="overflow-hidden rounded-lg border"
        data-testid="address-choose"
      >
        <p class="border-b bg-muted px-3 py-2 text-sm font-medium">
          {{ t('addressLookup.choose', { count: lookup.houses.value.length }) }}
        </p>
        <button
          v-for="option in lookup.houses.value" :key="houseKey(option)"
          type="button" role="radio" aria-checked="false"
          class="flex w-full items-center gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted"
          :class="inputClass ? 'min-h-14' : ''"
          data-testid="address-house-option"
          @click="lookup.pickHouse(option)"
        >
          <span class="h-4 w-4 shrink-0 rounded-full border border-input" aria-hidden="true" />
          <span class="min-w-0">
            <span class="block text-sm font-medium">{{ option.street_label }}</span>
            <span class="block text-xs text-muted-foreground">{{ cityLine(option) }}</span>
          </span>
        </button>
      </div>

      <!-- ask_street: the pair answered nothing or too many — the street field
           is open above. -->
      <p
        v-else-if="state === 'ask_street'"
        class="text-xs text-muted-foreground"
        data-testid="address-ask-street"
      >
        {{ t('addressLookup.askStreet', { number: civicNumber }) }}
      </p>

      <!-- not_found: definitive. Ink, not red — unknown is not an error. The
           verb is the only way forward besides fixing the text. -->
      <div
        v-else-if="state === 'not_found'"
        class="flex flex-wrap items-center justify-between gap-2"
        data-testid="address-not-found"
      >
        <span class="flex items-center gap-1.5 text-sm font-medium">
          <MapPinOff class="h-4 w-4 text-muted-foreground" />
          {{ t('addressLookup.notFound') }}
        </span>
        <Button type="button" size="sm" variant="outline" data-testid="address-accept-unlocated" @click="lookup.acceptUnlocated()">
          {{ saveVerb === 'create' ? t('addressLookup.createUnlocated') : t('addressLookup.saveUnlocated') }}
        </Button>
      </div>

      <!-- unlocated: folded into the pill above; its « changer » reopens the
           question with the inputs. -->

      <!-- failed: the geocoder could not be asked. Quiet, no verb, the save goes
           through — the async job asks again. Different words from « not found »
           on purpose. -->
      <p
        v-else-if="state === 'failed'"
        class="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="address-failed"
      >
        <MapPin class="h-3 w-3" />
        {{ t('addressLookup.unavailable') }}
      </p>

      <!-- found / candidate: the house is the pill above; here only the place
           question it raises. -->
      <template v-else-if="house && (state === 'found' || state === 'candidate')">
        <!-- THE PLACE QUESTION: a match on the PLACE, not the person — usually
             « new owner at a known address ». Naming the CURRENT customer is
             what lets the dispatcher arbitrate. -->
        <div
          v-if="state === 'candidate'"
          class="mt-2 grid gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950"
          data-testid="place-candidate-prompt"
        >
          <p class="text-sm">{{ t('customers.dedup.placeFound') }}</p>
          <div
            v-for="candidate in lookup.placeCandidates.value" :key="candidate.id"
            class="flex flex-wrap items-center justify-between gap-2"
          >
            <span class="text-sm">
              <span class="font-medium">{{ candidate.display_name }}</span>
              <span v-if="candidate.customer_label" class="text-muted-foreground">
                · {{ t('customers.dedup.currently', { name: candidate.customer_label }) }}
              </span>
            </span>
            <Button
              type="button" size="sm" data-testid="use-existing-location"
              @click="lookup.answerPlace({ use: 'existing', candidate })"
            >
              {{ t('customers.dedup.useLocation') }}
            </Button>
          </div>
          <div>
            <Button
              type="button" size="sm" variant="outline" data-testid="new-location"
              @click="lookup.answerPlace({ use: 'new' })"
            >
              {{ t('customers.dedup.newLocation') }}
            </Button>
          </div>
        </div>
        <!-- Resolved, restatable. -->
        <div
          v-else-if="lookup.placeAnswer.value"
          class="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground"
          data-testid="place-resolved-line"
        >
          <span>
            {{ lookup.placeAnswer.value.use === 'existing'
              ? t('customers.dedup.usingLocation', { name: lookup.placeAnswer.value.candidate.display_name })
              : t('customers.dedup.newLocationConfirmed') }}
          </span>
          <button type="button" class="underline" data-testid="reopen-place" @click="lookup.reopenPlace()">
            {{ t('customers.dedup.change') }}
          </button>
        </div>
      </template>
    </div>

    <!-- THE DOOR, always visible with the inputs: the caller who does not know
         the postal code. Open, it says what it does — back to the pair, the
         street fields cleared — never a mode the inputs may contradict. -->
    <button
      v-show="!placeCollapsed && !collapsed"
      type="button" class="justify-self-start text-xs underline"
      data-testid="address-door" @click="toggleStreet()"
    >
      {{ streetOpen ? t('addressLookup.postalOnly') : t('addressLookup.withoutPostal') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFieldValue, useSetFieldValue } from 'vee-validate'
import { Loader2, Check, MapPin, MapPinOff } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import type { AddressLookup, House } from '@/composables/useAddressLookup'
import { formatPostalCode, isCompletePostalCode, joinAddressLine } from '@/lib/address'

/**
 * The address row and its answers (design 170), shared by the intake block
 * (console modal, handheld drawer, the évaluation door) and the edit forms.
 * It writes into the HOST's vee-validate form under `fieldPrefix` and renders
 * the lookup's state; the lookup itself is owned by the host, which owns the
 * save and its gate.
 */
const props = withDefaults(defineProps<{
  lookup: AddressLookup
  // 'newAddress.' in the intake forms, '' in the edit forms.
  fieldPrefix?: string
  // Touch sizing for the handheld (`h-14 rounded-lg`, the 56px minimum).
  inputClass?: string
  // Which « sans localisation » word the verb carries.
  saveVerb?: 'create' | 'save'
  // The record-page form labels its fields; the intake block uses placeholders.
  labels?: boolean
}>(), { fieldPrefix: '', inputClass: undefined, saveVerb: 'create', labels: false })

const { t } = useI18n()

const state = computed(() => props.lookup.state.value)
const house = computed(() => props.lookup.house.value)

const civicNumber = useFieldValue<string | undefined>(() => `${props.fieldPrefix}civicNumber`)
const streetValue = useFieldValue<string | undefined>(() => `${props.fieldPrefix}street`)
const cityValue = useFieldValue<string | undefined>(() => `${props.fieldPrefix}city`)
const postalValue = useFieldValue<string | undefined>(() => `${props.fieldPrefix}postalCode`)
const setStreet = useSetFieldValue<string>(() => `${props.fieldPrefix}street`)
const setCity = useSetFieldValue<string>(() => `${props.fieldPrefix}city`)

// The street fields read the CONTENT, never a mode: open when they hold
// something (a stored address, a re-seeded form), when the pair could not
// settle the house, or when the person opened the door. A stored « 6204,
// G6Y 0K6 » with no street shows the pair alone.
const holdsStreet = computed(() => !!streetValue.value?.trim() || !!cityValue.value?.trim())
const streetOpen = ref(holdsStreet.value)
watch(holdsStreet, (held) => {
  if (held) streetOpen.value = true
})
watch(state, (next) => {
  if (next === 'ask_street') streetOpen.value = true
})
const toggleStreet = () => {
  streetOpen.value = !streetOpen.value
  if (!streetOpen.value) {
    setStreet('')
    setCity('')
    props.lookup.touch()
  }
}

// An 'existing' answer folds the fields to the resolved line — editing them
// would be theater, the submit chain uses the existing record.
const placeCollapsed = computed(() => props.lookup.placeAnswer.value?.use === 'existing')

// The pill: an answered address (found, or declared unlocated), or a stored one
// the form opened settled on. « changer » reopens what there is to reopen —
// the question, the list — or puts the text up for re-entry, so the next blur
// or the save asks again and the answer folds the pill back. No local flag: a
// flag that only a new answer cleared stranded the form when nothing changed.
const pillTone = computed<'found' | 'unlocated' | 'settled' | null>(() => {
  if (state.value === 'found' || state.value === 'candidate') return house.value ? 'found' : null
  if (state.value === 'unlocated') return 'unlocated'
  if (state.value === 'idle' && props.lookup.settled.value) return 'settled'
  return null
})
const collapsed = computed(() => pillTone.value !== null && !placeCollapsed.value)

// The pill may fold the inputs while the cursor is still in one (the postal
// code completing is the common case): release the focus, so the next Tab
// continues from the pill instead of a hidden field.
const rootEl = ref<HTMLElement | null>(null)
watch(collapsed, (folded) => {
  if (!folded) return
  const active = document.activeElement
  if (active instanceof HTMLElement && rootEl.value?.contains(active)) active.blur()
})

// Street mode, a street typed, no town and no full postal code: the lookup
// cannot ask honestly, and says what it is waiting for.
const needsTown = computed(() =>
  streetOpen.value && state.value === 'idle' && !!civicNumber.value?.trim() && !!streetValue.value?.trim()
  && !cityValue.value?.trim() && !isCompletePostalCode(postalValue.value))
const expand = () => {
  if (state.value === 'unlocated') props.lookup.reopenUnlocated()
  else if (state.value === 'found' && props.lookup.houses.value.length > 1) props.lookup.reopenHouses()
  else props.lookup.unsettle()
}
const pillLine = computed(() =>
  joinAddressLine(civicNumber.value ?? '', house.value?.street_label ?? streetValue.value ?? ''))
const pillCity = computed(() => {
  if (house.value) return cityLine(house.value)
  return [cityValue.value?.trim(), postalValue.value ? formatPostalCode(postalValue.value) : null].filter(Boolean).join(' ')
})

const cityLine = (option: House) =>
  [option.city, option.postal_code ? formatPostalCode(option.postal_code) : null].filter(Boolean).join(' ')
const houseKey = (option: House) => `${option.street_label}|${option.city}|${option.postal_code}`

// A question raised under a tall modal must come to the eye: the footer the
// person just pressed is below the fold.
const answerEl = ref<HTMLElement | null>(null)
const STOPPING_FOR_THE_EYE = new Set(['choose', 'ask_street', 'not_found', 'candidate'])
watch(state, async (next) => {
  if (!STOPPING_FOR_THE_EYE.has(next)) return
  await nextTick()
  answerEl.value?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
})
</script>
